import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Alert, Link as MuiLink, Skeleton } from "@mui/material";
import "./providerCalendar.scss";
import AppointmentDetails from "../../../components/sharedComponents/appointmentDetails/AppointmentDetails";
import { EventApi } from "@fullcalendar/core";
import moment from "moment";
import { CALENDAR_VIEW, Days, FullDays } from "../../../constant/calenderView";
import { Appointment } from "types/appointments";
import { AttestationDateRange, AttestationDay } from "types/attestation";
import {
  findAttestationDay,
  toDayStripKey,
} from "../../../components/forms/providerAppointmentForms/attestation.helper";
import {
  ButtonDates,
  ClickPosition,
  // ClientColor,
  Idate,
} from "types/BiCalendar";
import AppointmentComponent from "./AppointmentComponent";
import { appointments } from "types/calender/appointments";
import Popup from "../../../components/sharedComponents/reactHookComponents/popup/popup";
import MileageError from "../../../components/sharedComponents/Error/mileageError";
import { APPT_LOCATION_TYPES } from "constant/AppConstants";
import Failure from "components/Failure/Failure";
import {isTablet, isDesktop} from "react-device-detect";

interface IProps {
  appointments: Appointment[];
  handleDataChange: (
    startStr: string,
    endStr: string,
    setLoading: boolean
  ) => void;
  softloading: boolean;
  providerId: number;
  providerName: string;
  providerEmail: string;
  providerClinicId: number;
  disableAddAppt: boolean;
  dayConfirmed: boolean;
  toDate?: string;
  prefDate?: string;
  attestationDays?: AttestationDay[];
  /**
   * Attestation banner or catch-up header, rendered under the page heading and above the
   * availability notice. Passed in rather than built here so the parent keeps ownership of the
   * attestation state, while this component decides where it sits and how wide it is.
   */
  attestationSlot?: React.ReactNode;
  /**
   * Reports the inclusive YYYY-MM-DD window the day strip is showing, whenever it changes.
   *
   * The parent asks the attestation endpoint for exactly this window, so every day on screen can
   * carry a marker — including days outside the current pay period. A period is two whole
   * Sunday-to-Saturday weeks, so the week on screen never straddles a boundary, but stepping
   * back a week lands squarely in the period before, whose unattested days are still owed.
   *
   * Only this component knows the window: the parent is told about the *selected day*, not the
   * week around it.
   */
  onVisibleRangeChange?: (range: AttestationDateRange) => void;
  /** Suppresses the availability notice while the BT is mid catch-up; see where it renders. */
  hideAvailabilityNotice?: boolean;
  /**
   * YYYY-MM-DD the parent wants selected. Changing it selects that day; the value is a
   * request rather than controlled state, so the parent clears it once handled.
   */
  requestedDate?: string;
  onRequestedDateHandled?: () => void;
  getAppointments: (
    startStr: string,
    endStr: string,
    setLoading: boolean
  ) => void;
  startDateProps: any;
  endDateProps: any;
  setShouldPoll: (val: boolean) => void;
  setResyncCrId: (val: number) => void;
  filteredResyncAppointments: any;
  resyncDisable: boolean;
  setResyncDisable: (val: boolean) => void;
  resyncCount: number;
  setResyncCount: (val: number) => void;
  displayMileageError: boolean;
  providerClinicTimeZone: string;
  backendError: boolean;
  reloadAppointments: () => void;
  onCloseError: () => void;
}

const ProviderCalendarView: React.FC<IProps> = ({
  appointments,
  handleDataChange,
  softloading,
  providerId,
  providerName,
  providerClinicId,
  providerEmail,
  disableAddAppt,
  dayConfirmed,
  toDate,
  prefDate,
  attestationDays = [],
  attestationSlot,
  onVisibleRangeChange,
  hideAvailabilityNotice,
  requestedDate,
  onRequestedDateHandled,
  getAppointments,
  startDateProps,
  endDateProps,
  setShouldPoll,
  setResyncCrId,
  filteredResyncAppointments,
  resyncDisable,
  setResyncDisable,
  resyncCount,
  setResyncCount,
  displayMileageError,
  providerClinicTimeZone,
  backendError,
  reloadAppointments,
  onCloseError,
}) => {
  const navigate = useNavigate();
  const [showAppointmentDetails, setShowAppointmentDetails] =
    useState<Boolean>(false);
  const [eventDetail, setEventDetail] = useState<appointments | null>(null);

  const [clickPosition, setClickPosition] = useState<ClickPosition>({
    x: 0,
    y: 0,
  });
  const [activeEvent, setActiveEvent] = useState<string | null>(null);
  const [buttonDates, setButtonDates] = useState<ButtonDates[]>([]);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const selectedOption: string = "Day";

  /**
   * Attestation marker for a day in the strip. Days the provider was not scheduled to work
   * carry no marker, and the wrapper is omitted entirely so unmarked strips keep their
   * original height.
   */
  const renderAttestationMarker = (date: Date) => {
    const attestationDay = findAttestationDay(date, attestationDays);

    if (!attestationDay) {
      return null;
    }
    const label = attestationDay.isAttested ? "Attested" : "Needs attestation";

    return (
      <div className="dayAppointments-attestation">
        <span
          className={`attestation-dot ${
            attestationDay.isAttested ? "attested" : "needs-attestation"
          }`}
          data-testid={`attestation-dot-${attestationDay.date}`}
          role="img"
          aria-label={label}
          title={label}
        />
        <span className="attestation-dot-text">{label}</span>
      </div>
    );
  };

  const [activeDay, setActiveDay] = useState<string>(new Date().toString());

  const [isPopupOpen, setPopupOpen] = React.useState<boolean>(false);

  const calendarRef = useRef<null>(null);
  // Needed to move the week view itself; `calendarRef` above is on the wrapping element.
  const fullCalendarRef = useRef<FullCalendar | null>(null);
  /**
   * A day the parent asked for that the calendar is still navigating to. Held so the
   * `datesSet` that navigation triggers selects that day rather than the week's start.
   */
  const pendingRequestedDateRef = useRef<string | null>(null);
  const buttonDatesRef = useRef<ButtonDates[]>(buttonDates);
  const startDateRef = useRef<string>(startDate);
  const endDateRef = useRef<string>(endDate);

  const handleEventClick = (info: any) => {
    const targetElement = info.el;
    const targetRect = targetElement.getBoundingClientRect();

    const popUpX =
      targetRect.left + window.scrollX < window.innerWidth / 2
        ? targetRect.right + window.scrollX - 18
        : targetRect.left + window.scrollX - 220;
    const popUpY = targetRect.top + window.scrollY;

    setClickPosition({ x: popUpX, y: popUpY });
    setEventDetail(info.event?.extendedProps);
    setShowAppointmentDetails(true);
    if (activeEvent === info.event.id) {
      setActiveEvent(null);
    } else {
      setActiveEvent(info.event.id);
      // info.el.classList.add("active-event");
    }
  };

  const handleAddNewAppointment = () => {
    navigate("/add-new-appointment",{
      state: {
        providerId: providerId, // Pass providerId in state
        providerName: providerName, // Pass providerName in state
        providerClinicId: providerClinicId,
        providerEmail: providerEmail,
        prevDate: moment(activeDay).toISOString(),
        providerClinicTimeZone: providerClinicTimeZone
      },
    });
  };

  const handleEventContent = ({ event }: { event: EventApi }) => {
    return (
      <div className="appt-card">
        <p className="appt-type">{event?.title}</p>
        <p className="appt-location">
          {event?.extendedProps.location_type === APPT_LOCATION_TYPES.inclinic
            ? `${event?.extendedProps?.clinic?.name} Clinic`
            : event?.extendedProps.location_type === APPT_LOCATION_TYPES.offsite
            ? `${event?.extendedProps?.location_category}`
            : `TeleHealth`}
        </p>
        <p className="appt-provider">{event?.extendedProps?.provider_name}</p>
      </div>
    );
  };

  const checkIfWithinCurrentWeek = (startDate: string) => {
    const startOfWeek = moment().startOf("week");

    const startDateWithinWeek = moment(startDate).isSame(startOfWeek);
    return startDateWithinWeek;
  };

  /** Loads a single day and marks it selected in the strip. */
  const selectSingleDay = (target: moment.Moment) => {
    handleDataChange(
      target.clone().startOf("day").toISOString(),
      target.clone().endOf("day").toISOString(),
      true
    );
    setShowAppointmentDetails(false);
    setEventDetail(null);
    setActiveEvent(null);
    setActiveDay(target.clone().startOf("day").toDate().toString());
  };

  const handlePlannerDateViewChange = useCallback(
    (dateArgs: any) => {
      const { startStr, endStr } = dateArgs;

      const parsedStartData: moment.Moment = moment(startStr);

      let TodaysStartDate = moment().format("YYYY-MM-DD");
      const formattedStart = parsedStartData.format("YYYY-MM-DD");
      parsedStartData.set({ minute: 0, second: 0, millisecond: 0 });

      const parsedEndData: moment.Moment = moment(endStr);

      const ifCurrentWeek = checkIfWithinCurrentWeek(formattedStart);

      parsedEndData.set({ minute: 0, second: 0, millisecond: 0 });

      setStartDate(parsedStartData.toISOString());
      setEndDate(parsedEndData.toISOString());

      /*
       * Built with the strip's own key helper so the window asked for is exactly the set of days
       * the strip will draw — a marker keyed a day out would sit under the wrong number.
       * FullCalendar's `endStr` is exclusive, so the last cell is the day before it.
       */
      onVisibleRangeChange?.({
        startDate: toDayStripKey(parsedStartData.toDate()),
        endDate: toDayStripKey(
          parsedEndData.clone().subtract(1, "day").toDate()
        ),
      });

      const pendingRequestedDate = pendingRequestedDateRef.current;

      if (pendingRequestedDate) {
        // Consumed either way, so a request that never produced a navigation cannot sit here
        // and hijack a later one the BT made themselves.
        pendingRequestedDateRef.current = null;
        const pendingTarget = moment(pendingRequestedDate, "YYYY-MM-DD", true);
        const isTheNavigationWeAskedFor =
          pendingTarget.isValid() &&
          pendingTarget.isBetween(parsedStartData, parsedEndData, "day", "[)");

        if (isTheNavigationWeAskedFor) {
          // Reached to show this specific day, so none of the defaults below apply.
          selectSingleDay(pendingTarget);
          return;
        }
      }
      const currentDay = parsedStartData.day();
      if (ifCurrentWeek) {
        setActiveDay(TodaysStartDate);
        let TodaysStateDateTime = moment(TodaysStartDate)
          .startOf("day")
          .toISOString();
        let TodaysEndDateTime = moment(TodaysStartDate)
          .endOf("day")
          .toISOString();
        if (prefDate === undefined) {
          handleDataChange(TodaysStateDateTime, TodaysEndDateTime, true);
        }
      } else if (
        currentDay === 0 &&
        moment(activeDay).format("dddd") === FullDays[0]
      ) {
        const currentSunday = parsedStartData.clone();
        const formattedStartDate = currentSunday.startOf("day").toISOString();
        const formattedEndDate = currentSunday.endOf("day").toISOString();
        handleDataChange(formattedStartDate, formattedEndDate, true);
        setActiveDay(currentSunday.toDate().toString());
      } else {
        const currentDate = prefDate
          ? new Date(prefDate)
          : parsedStartData.clone();
        // const currentSunday = parsedStartData.clone();
        handleDataChange(
          moment(currentDate).startOf("day").toISOString(),
          moment(currentDate).endOf("day").toISOString(),
          true
        );
        setActiveDay(parsedStartData.toDate().toString());
      }
    },
    [activeDay, prefDate, handleDataChange, onVisibleRangeChange]
  );

  useEffect(() => {
    const start: Date = new Date(startDate);
    const end: Date = new Date(endDate);

    const dates = [];

    while (start <= end) {
      if (start.getDay() === 0 && dates.length === 0) {
        dates.push({ date: new Date(start), week: moment(start).isoWeek() });
      } else if (start.getDay() !== 0) {
        dates.push({ date: new Date(start), week: moment(start).isoWeek() });
      }
      start.setDate(start.getDate() + 1);
    }

    setButtonDates(dates);
  }, [startDate, endDate]);

  useEffect(() => {
    buttonDatesRef.current = buttonDates;
  }, [buttonDates]);

  useEffect(() => {
    startDateRef.current = startDate;
  }, [startDate]);

  useEffect(() => {
    endDateRef.current = endDate;
  }, [endDate]);

  useEffect(() => {
    const currentWeek = moment().isoWeek();
    const currentDate = new Date();
    const buttonDates = buttonDatesRef.current;
    if (buttonDates.length <= 0 && selectedOption === CALENDAR_VIEW.dayView) {
      prefDate
        ? setActiveDay(new Date(prefDate).toString())
        : setActiveDay(new Date().toString());
      if (prefDate) {
        handleDataChange(
          moment(prefDate).startOf("day").toISOString(),
          moment(prefDate).endOf("day").toISOString(),
          true
        );
      }
    } else {
      if (currentWeek === buttonDates[2]?.week) {
        setActiveDay(currentDate.toString());
      } else {
        setActiveDay(buttonDates[0]?.date.toString());
      }
    }
  }, [selectedOption]);

  const handleButtonClick = (date: Idate) => {
    handleDataChange(
      moment(date.date).startOf("day").toISOString(),
      moment(date.date).endOf("day").toISOString(),
      true
    );
    setShowAppointmentDetails(false);
    setEventDetail(null);
    setActiveEvent(null);
    setActiveDay(date?.date.toString());
  };

  /**
   * Selects a day the parent asked for (the attestation banner's call to action).
   *
   * Outstanding days routinely sit in the week before the one on screen — a pay period spans
   * two — so the week view has to move as well, otherwise the appointment list would swap to
   * the target day while the strip stayed put with nothing highlighted. `prefDate` cannot do
   * this: it only feeds FullCalendar's once-only `initialDate`.
   */
  useEffect(() => {
    if (!requestedDate) {
      return;
    }
    const target = moment(requestedDate, "YYYY-MM-DD", true);
    if (!target.isValid()) {
      onRequestedDateHandled?.();
      return;
    }
    const calendarApi = fullCalendarRef.current?.getApi();
    const targetDate = target.clone().startOf("day").toDate();
    /*
     * Navigating fires `datesSet` asynchronously, and that handler would otherwise select the
     * week's start. Handing it the target through the ref lets it select the right day
     * whenever it runs. Moving inside the displayed week does not fire it at all, so that
     * case is selected here instead.
     */
    const staysWithinDisplayedWeek =
      !!calendarApi &&
      targetDate >= calendarApi.view.currentStart &&
      targetDate < calendarApi.view.currentEnd;

    if (calendarApi && !staysWithinDisplayedWeek) {
      pendingRequestedDateRef.current = requestedDate;
      calendarApi.gotoDate(targetDate);
    } else {
      selectSingleDay(target);
    }
    onRequestedDateHandled?.();
    // handleDataChange is recreated by the parent each render; depending on it would
    // re-run this effect continuously.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedDate]);

  const handleTodayButtonClick = useCallback(() => {
    const currentSelectedOption = selectedOption;
    if (currentSelectedOption === CALENDAR_VIEW.dayView) {
      const currentDate = new Date();
      handleDataChange(
        moment(currentDate).startOf("day").toISOString(),
        moment(currentDate).endOf("day").toISOString(),
        true
      );
      setActiveDay(new Date().toString());
    }
  }, [selectedOption, handleDataChange]);

  useEffect(() => {
    const todayButton = document.querySelector(".fc-today-button");
    if (todayButton) {
      todayButton.addEventListener("click", handleTodayButtonClick);
    }

    return () => {
      if (todayButton) {
        todayButton.removeEventListener("click", handleTodayButtonClick);
      }
    };
  }, [selectedOption, handleTodayButtonClick]);

  const handleClosePopup = () => {
    setPopupOpen(false);
  };

  return (
    <>
      <div className={!isTablet || !isDesktop ? "primary-wrapper-provider" : "primary-wrapper-provider-mobile"}>
        <div ref={calendarRef} className="provider-calender-view">
          <div className="page-header">
            <h2 className="page-header-h2">My Schedule</h2>
          </div>
          <div className="provider-availability-view-content">
          {attestationSlot}
          {/*
            * Hidden while a catch-up run is active. It is unrelated to attesting, it is the
            * second-loudest thing on the screen, and its icon is the same orange as the
            * "needs attestation" dot — so recruitment and payroll end up sharing a colour at
            * exactly the moment the BT is being asked to concentrate on one of them.
            */}
          {!hideAvailabilityNotice && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              backgroundColor: "#FFF4E5",
              color: "#663C00",
              borderRadius: "8px",
              "& .MuiAlert-icon": {
                color: "#ED6C02",
              },
              textAlign: "center",
            }}
          >
            Looking to add more hours?{" "}
            <MuiLink
              href="/availability"
              sx={{
                color: "#663C00",
                textDecoration: "underline",
                fontWeight: 500,
                "&:hover": {
                  color: "#424242",
                },
              }}
            >
              View clients waiting for services & send a request
            </MuiLink>
          </Alert>
          )}
          </div>
          <div className="special-class">
            <div className="calendar-header-wrapper">
              <FullCalendar
                ref={fullCalendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                allDaySlot={false}
                initialView="timeGridCustomView"
                views={{
                  timeGridCustomView: {
                    type: "timeGrid",
                    duration: { weeks: 1 },
                  },
                }}
                slotMinTime="07:00:00"
                slotMaxTime="22:00:00"
                eventClick={handleEventClick}
                slotEventOverlap={false}
                eventContent={handleEventContent}
                customButtons={{
                  addAppointment: {
                    text: '+ Add New Appointment',
                    click: () => {
                      if (!disableAddAppt && !softloading) {
                        handleAddNewAppointment();
                      }
                    },
                  },
                }}
                headerToolbar={{
                  left: "today,prev,next",
                  center: "title",
                  right: "addAppointment",
                }}
                buttonText={{
                  today: "Today",
                }}
                initialDate={prefDate}
                datesSet={handlePlannerDateViewChange}
              />
            </div>
          </div>

          {selectedOption === CALENDAR_VIEW.dayView ? (
            <div>
              <div
                className="dayAppointments"
              >
                {buttonDates.map((date, index) => (
                  <div
                    className={`dayAppointments-1 ${
                      moment(activeDay).isSame(
                        moment(date.date.toDateString(), "ddd MMM DD YYYY"),
                        "day"
                      )
                        ? "active"
                        : ""
                    }`}
                    style={{
                      cursor: softloading ? "not-allowed" : "pointer",
                    }}
                    key={index}
                  >
                    <div
                      key={index}
                      onClick={() => handleButtonClick(date)}
                      className="dayAppointments-buttons"
                      style={{ pointerEvents: softloading ? "none" : "auto" }}
                    >
                      <div className="dayAppointments-2">
                        {Days[date.date.getUTCDay()]}
                      </div>
                      <div className="dayAppointments-3">
                        {date.date.getUTCDate()}
                      </div>
                      {renderAttestationMarker(date.date)}
                    </div>
                  </div>
                ))}
              </div>

              {/*
                * Sits directly above the appointments rather than up by the page heading: it
                * refers to a specific appointment on the selected day, and at the top of the
                * page it read as a general warning with no obvious connection to the list.
                */}
              {displayMileageError && <MileageError />}

              {!softloading &&
              activeDay &&
              (toDate ? moment(activeDay).isSame(toDate, "day") : true) ? (
                appointments?.length === 0 ? (
                  <div className="no-appointments">
                    <p className="no-appointments-1">
                      No Upcoming Appointments Today
                    </p>
                  </div>
                ) : (
                  <div className="appointment-list-provider">
                    {appointments?.map((appointment: any) => {
                      return (
                        <AppointmentComponent
                          key={appointment.id}
                          appointment={appointment}
                          dayConfirmed={dayConfirmed}
                          activeDay={activeDay}
                          getAppointments={getAppointments}
                          startDateProps={startDateProps}
                          endDateProps={endDateProps}
                          setShouldPoll={setShouldPoll}
                          setResyncCrId={setResyncCrId}
                          filteredResyncAppointments={
                            filteredResyncAppointments
                          }
                          resyncDisable={resyncDisable}
                          setResyncDisable={setResyncDisable}
                          resyncCount={resyncCount}
                          setResyncCount={setResyncCount}
                          isPopupOpen={isPopupOpen}
                          setPopupOpen={setPopupOpen}
                        />
                      );
                    })}
                  </div>
                )
              ) : (
                <div className="appointment-skeleton-container">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="appointment-skeleton-card">
                      <Skeleton 
                        variant="rectangular" 
                        width="100%" 
                        height={100} 
                        sx={{ borderRadius: "8px", bgcolor: "#f0f0f0" }}
                        animation="wave"
                      />
                    </div>
                  ))}
                  <div className="appointment-skeleton-bottom">
                    <Skeleton 
                      variant="rectangular" 
                      width={120} 
                      height={40} 
                      sx={{ borderRadius: "8px", bgcolor: "#e8e8e8" }}
                      animation="wave"
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            ""
          )}

          {showAppointmentDetails && (
            <AppointmentDetails
              show={showAppointmentDetails}
              onClose={() => setShowAppointmentDetails(false)}
              event={eventDetail}
              clickPosition={clickPosition}
              setActiveEvent={setActiveEvent}
            />
          )}
        </div>
        <Popup
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          heading="Appointment Failed to Sync"
          paragraph="The appointment has failed to sync to Central Reach. Please send a IT Help Desk ticket to report issue."
        />
      </div>
      {backendError && (
        <Failure refresh={reloadAppointments} onClose={onCloseError} />
      )}
    </>
  );
};

export default React.memo(ProviderCalendarView);
